import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Wand2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SUGGESTIONS = [
  "What's the status of my latest order?",
  "Which of my devices has the worst Device UX score?",
  "Which of my devices are approaching refresh?",
  "Help me order a new laptop",
];

export default function AskMeDialog({ open, onOpenChange, onAction }) {
  const { persona, userName } = usePersona();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    if (open) {
      setMessages([
        { role: "bot", text: `Hi ${userName?.split(" ")[0] || "there"}, I'm Flexspace Copilot. I can see your orders, tickets, devices and Device UX health. What can I help with?` },
      ]);
    }
  }, [open, userName]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setThinking(true);
    try {
      const res = await endpoints.assistantChat({
        message: q,
        persona_id: persona.id,
        user_name: userName || "Employee",
        session_id: sessionId,
      });
      if (res.session_id) setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "bot", text: res.answer, action: res.suggested_action, provider: res.provider }]);
    } catch {
      toast.error("Copilot unavailable");
    } finally { setThinking(false); }
  };

  const runAction = (action) => {
    onAction?.(action);
    onOpenChange(false);
    if (action.id === "view_ticket" && action.params?.req_number) {
      toast.info(`Opening ${action.params.req_number}`);
    } else if (action.id === "place_order") {
      nav("/catalogue");
    } else if (action.id === "report_incident") {
      // handled via onAction upstream
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="askme-dialog" className="max-w-lg bg-white">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Sparkles size={12} className="text-[#0033A0]" /> Flexspace Copilot
          </div>
          <DialogTitle className="font-display text-2xl text-slate-900">Ask Me anything</DialogTitle>
          <DialogDescription>Grounded in your live orders, devices, Device UX health and open tickets.</DialogDescription>
        </DialogHeader>

        <div className="border border-slate-200 rounded-md bg-slate-50 p-3 max-h-80 overflow-y-auto space-y-2" data-testid="askme-messages">
          {messages.map((m, i) => (
            <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
              <div className={`inline-block max-w-[90%] px-3 py-2 rounded-md text-left ${m.role === "user" ? "bg-[#0033A0] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                <div className="whitespace-pre-wrap">{m.text}</div>
                {m.action && (
                  <button
                    onClick={() => runAction(m.action)}
                    data-testid={`copilot-action-${m.action.id}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0033A0] hover:underline"
                  >
                    <Wand2 size={12} /> {m.action.id.replace(/_/g, " ")} →
                  </button>
                )}
              </div>
            </div>
          ))}
          {thinking && <div className="text-xs text-slate-500 italic flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Thinking…</div>}
        </div>

        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid={`askme-suggest-${s.slice(0, 8)}`} className="text-xs border border-slate-200 bg-white hover:bg-slate-50 rounded-full px-3 py-1 text-slate-700">
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Input
            data-testid="askme-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !thinking) send(); }}
            placeholder="Ask about your orders, Device UX, refresh, disposal…"
          />
          <Button data-testid="askme-send" onClick={() => send()} disabled={thinking} className="bg-[#0033A0] hover:bg-[#002573] text-white">
            <Send size={14} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
